import { TweakPanel } from '@/commons/TweakPanel';
import { Wedding3DWorld } from '@/littleWedding/Wedding3DWorld';
import {
    type Object3D,
    type Mesh,
    type SpotLightHelper,
    type DirectionalLightHelper,
    type MeshStandardMaterial,
    type MeshPhysicalMaterial,
    type RectAreaLight,
} from 'three';
import type { IThreeLights } from '@/commons/lights/typescript/type/TThreeLights';
import type { FolderApi } from 'tweakpane';

type ObjectType = IThreeLights | Mesh | Object3D;

function addNameAndFolderName(object: ObjectType, panelFolder: FolderApi) {
    // check name and give it to object if none
    if (!object.name) {
        object.name = `${object.type}-${object.uuid.split('-')[0]}`;
    }
    const isFolderAlreadyCreated = TweakPanel.getFolderOrTabByName(object.name, panelFolder);
    if (isFolderAlreadyCreated) {
        return null;
    }
    return panelFolder!.addFolder({
        title: object.name,
        expanded: false,
    });
}

function addPositionControlPanel(object: ObjectType, panelFolder: FolderApi) {
    // Position
    panelFolder?.addBlade({ view: 'separator' });
    panelFolder?.addBinding(
        {
            get y() {
                return object.position.y;
            },
            set y(value) {
                object.position.y = value;
                const objectHelper = Wedding3DWorld.scene?.getObjectByName(`${object.name}-helper`) as
                    | DirectionalLightHelper
                    | SpotLightHelper;
                if (objectHelper?.update) {
                    objectHelper?.update();
                } else {
                    object.lookAt(0, 0.75, 0);
                    objectHelper?.updateMatrixWorld();
                }
            },
        },
        'y',
        { label: 'Position Y', min: -5, max: 5, step: 0.1 },
    );
    panelFolder?.addBinding(
        {
            position: {
                get x() {
                    return object.position.x;
                },
                set x(value) {
                    object.position.x = value;
                    const objectHelper = Wedding3DWorld.scene?.getObjectByName(`${object.name}-helper`) as
                        | DirectionalLightHelper
                        | SpotLightHelper;
                    if (objectHelper?.update) {
                        objectHelper?.update();
                    } else {
                        object.lookAt(0, 0.75, 0);
                        objectHelper?.updateMatrixWorld();
                    }
                },
                get y() {
                    return object.position.z;
                },
                set y(value) {
                    object.position.z = value;
                    const objectHelper = Wedding3DWorld.scene?.getObjectByName(`${object.name}-helper`) as
                        | DirectionalLightHelper
                        | SpotLightHelper;
                    if (objectHelper?.update) {
                        objectHelper?.update();
                    } else {
                        object.lookAt(0, 0.75, 0);
                        objectHelper?.updateMatrixWorld();
                    }
                },
            },
        },
        'position',
        { label: 'Position XZ', min: -2, max: 2, step: 0.1, expanded: false, picker: 'inline' },
    );
}

function addDeleteLightButton(object: ObjectType, panelFolder: FolderApi) {
    panelFolder?.addButton({ title: 'Delete' }).on('click', () => {
        TweakPanel.removeFolderByName(panelFolder?.title || '');
        Wedding3DWorld.scene?.remove(object);
        const helper = Wedding3DWorld.scene?.getObjectByName(`${object.name}-helper`) as Object3D;
        if (helper) {
            Wedding3DWorld.scene?.remove(helper);
        }
        const cameraHelper = Wedding3DWorld.scene?.getObjectByName(`${object.name}-shadow-helper`) as Object3D;
        if (cameraHelper) {
            Wedding3DWorld.scene?.remove(cameraHelper);
        }
        TweakPanel.removeFolderByName(`${object.name}`);
    });
    panelFolder?.addButton({ title: 'Log' }).on('click', () => {
        console.info(`${object.name}: `, object);
    });
}

function addColorControlPanel(object: IThreeLights | MeshPhysicalMaterial | MeshStandardMaterial, panelFolder: FolderApi) {
    panelFolder?.addBinding(
        {
            color: {
                get r() {
                    return object.color.r * 255;
                },
                set r(value) {
                    object.color.r = value / 255;
                },
                get g() {
                    return object.color.g * 255;
                },
                set g(value) {
                    object.color.g = value / 255;
                },
                get b() {
                    return object.color.b * 255;
                },
                set b(value) {
                    object.color.b = value / 255;
                },
            },
        },
        'color',
        {
            label: 'Color',
            picker: 'inline',
            expanded: false,
        },
    );
}

function addSizeControlPanel(light: RectAreaLight, panelFolder: FolderApi) {
    panelFolder?.addBinding(
        {
            get width() {
                return light.width;
            },
            set width(value) {
                light.width = value;
            },
        },
        'width',
        { label: 'Width', min: 0, max: 10, step: 0.01 },
    );
    panelFolder?.addBinding(
        {
            get height() {
                return light.height;
            },
            set height(value) {
                light.height = value;
            },
        },
        'height',
        { label: 'height', min: 0, max: 10, step: 0.01 },
    );
}

export { addColorControlPanel, addDeleteLightButton, addNameAndFolderName, addPositionControlPanel, addSizeControlPanel };
